import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import { requireSupabaseAuth } from '@/integrations/supabase/auth-middleware'

const TokenSchema = z.object({ token: z.string().trim().min(20).max(128) })

const SubmitSchema = z.object({
  token: z.string().trim().min(20).max(128),
  rating: z.coerce.number().int().min(1).max(5),
  title: z.string().trim().max(120).optional().default(''),
  comment: z.string().trim().max(4000).optional().default(''),
  guestName: z.string().trim().max(120).optional().default(''),
  locale: z.enum(['nl', 'fr', 'en', 'de']).optional().default('nl'),
})

export type ReviewSubmitInput = z.infer<typeof SubmitSchema>

export const getReviewByToken = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => TokenSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { data: booking, error } = await supabaseAdmin
      .from('bookings')
      .select(
        'id, reference, name, check_in, check_out, country, preferred_language, review_token, review_submitted_at',
      )
      .eq('review_token', data.token)
      .maybeSingle()
    if (error) throw new Error(error.message)
    if (!booking) throw new Error('not_found')

    let existing: { rating: number; title: string | null; comment: string | null } | null = null
    if (booking.review_submitted_at) {
      const { data: rev } = await supabaseAdmin
        .from('reviews')
        .select('rating, title, comment')
        .eq('booking_id', booking.id)
        .maybeSingle()
      existing = rev ?? null
    }

    return {
      reference: booking.reference,
      guestName: booking.name,
      checkIn: booking.check_in,
      checkOut: booking.check_out,
      country: booking.country,
      preferredLanguage: booking.preferred_language,
      submittedAt: booking.review_submitted_at,
      existing,
    }
  })

export const submitReview = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => SubmitSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { data: booking, error } = await supabaseAdmin
      .from('bookings')
      .select('id, review_token')
      .eq('review_token', data.token)
      .maybeSingle()
    if (error) throw new Error(error.message)
    if (!booking) throw new Error('not_found')

    const payload = {
      booking_id: booking.id,
      rating: data.rating,
      title: data.title?.trim() || null,
      comment: data.comment?.trim() || null,
      guest_name: data.guestName?.trim() || null,
      locale: data.locale,
      published: false,
      updated_at: new Date().toISOString(),
    }

    const { error: upsertErr } = await supabaseAdmin
      .from('reviews')
      .upsert(payload, { onConflict: 'booking_id' })
    if (upsertErr) throw new Error(upsertErr.message)

    await supabaseAdmin
      .from('bookings')
      .update({ review_submitted_at: new Date().toISOString() })
      .eq('id', booking.id)

    return { ok: true }
  })

export type AdminReview = {
  id: string
  booking_id: string
  rating: number
  title: string | null
  comment: string | null
  guest_name: string | null
  locale: string | null
  published: boolean
  created_at: string
  updated_at: string
  booking: {
    reference: string | null
    name: string | null
    email: string | null
    country: string | null
    check_in: string | null
    check_out: string | null
  } | null
}

export const listReviews = createServerFn({ method: 'GET' })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc('has_role', {
      _user_id: context.userId,
      _role: 'admin',
    })
    if (!isAdmin) throw new Error('Forbidden')

    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { data, error } = await supabaseAdmin
      .from('reviews')
      .select(
        'id, booking_id, rating, title, comment, guest_name, locale, published, created_at, updated_at, booking:bookings(reference, name, email, country, check_in, check_out)',
      )
      .order('created_at', { ascending: false })
    if (error) throw new Error(error.message)
    return (data ?? []) as unknown as AdminReview[]
  })

export const setReviewPublished = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), published: z.boolean() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc('has_role', {
      _user_id: context.userId,
      _role: 'admin',
    })
    if (!isAdmin) throw new Error('Forbidden')
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { error } = await supabaseAdmin
      .from('reviews')
      .update({ published: data.published, updated_at: new Date().toISOString() })
      .eq('id', data.id)
    if (error) throw new Error(error.message)
    return { ok: true }
  })

export const deleteReview = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc('has_role', {
      _user_id: context.userId,
      _role: 'admin',
    })
    if (!isAdmin) throw new Error('Forbidden')
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { error } = await supabaseAdmin.from('reviews').delete().eq('id', data.id)
    if (error) throw new Error(error.message)
    return { ok: true }
  })
export type ReviewRequestCandidate = {
  id: string
  reference: string | null
  name: string | null
  email: string | null
  country: string | null
  preferred_language: string | null
  check_in: string | null
  check_out: string | null
  review_token: string | null
  review_submitted_at: string | null
  pre_checkout_email_sent_at: string | null
}

export const listReviewRequestCandidates = createServerFn({ method: 'GET' })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc('has_role', {
      _user_id: context.userId,
      _role: 'admin',
    })
    if (!isAdmin) throw new Error('Forbidden')
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const today = new Date().toISOString().slice(0, 10)
    const { data, error } = await supabaseAdmin
      .from('bookings')
      .select(
        'id, reference, name, email, country, preferred_language, check_in, check_out, review_token, review_submitted_at, pre_checkout_email_sent_at, status, deposit_paid_at',
      )
      .lte('check_out', today)
      .order('check_out', { ascending: false })
      .limit(100)
    if (error) throw new Error(error.message)
    return (data ?? [])
      .filter(
        (b: any) =>
          !!b.email &&
          (!!b.deposit_paid_at || ['confirmed', 'approved', 'paid', 'completed'].includes(b.status)),
      )
      .map((b: any) => ({
        id: b.id,
        reference: b.reference,
        name: b.name,
        email: b.email,
        country: b.country,
        preferred_language: b.preferred_language,
        check_in: b.check_in,
        check_out: b.check_out,
        review_token: b.review_token,
        review_submitted_at: b.review_submitted_at,
        pre_checkout_email_sent_at: b.pre_checkout_email_sent_at,
      })) as ReviewRequestCandidate[]
  })

export const getReviewLink = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ bookingId: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }): Promise<{ url: string }> => {
    const { data: isAdmin } = await context.supabase.rpc('has_role', {
      _user_id: context.userId,
      _role: 'admin',
    })
    if (!isAdmin) throw new Error('Forbidden')
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { data: b, error } = await supabaseAdmin
      .from('bookings')
      .select('id, review_token')
      .eq('id', data.bookingId)
      .maybeSingle()
    if (error) throw new Error(error.message)
    if (!b) throw new Error('not_found')

    let token = b.review_token
    if (!token) {
      const bytes = new Uint8Array(32)
      crypto.getRandomValues(bytes)
      token = Array.from(bytes)
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('')
      const { error: tokErr } = await supabaseAdmin
        .from('bookings')
        .update({ review_token: token })
        .eq('id', b.id)
      if (tokErr) throw new Error(tokErr.message)
    }

    return { url: `https://casamipajobe.com/review/${token}` }
  })

export const resendReviewRequest = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        bookingId: z.string().uuid(),
        locale: z.enum(['nl', 'fr', 'en', 'de']).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc('has_role', {
      _user_id: context.userId,
      _role: 'admin',
    })
    if (!isAdmin) throw new Error('Forbidden')
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { sendTransactionalEmail } = await import('@/lib/email-sender.server')
    const { resolveLocale } = await import('@/lib/email-templates/i18n')

    const { data: b, error } = await supabaseAdmin
      .from('bookings')
      .select(
        'id, reference, name, email, country, preferred_language, check_in, check_out, review_token',
      )
      .eq('id', data.bookingId)
      .maybeSingle()
    if (error) throw new Error(error.message)
    if (!b) throw new Error('not_found')
    if (!b.email) throw new Error('no_email')

    let token = b.review_token
    if (!token) {
      const bytes = new Uint8Array(32)
      crypto.getRandomValues(bytes)
      token = Array.from(bytes)
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('')
      const { error: tokErr } = await supabaseAdmin
        .from('bookings')
        .update({ review_token: token })
        .eq('id', b.id)
      if (tokErr) throw new Error(tokErr.message)
    }

    const locale =
      data.locale ?? resolveLocale({ preferred: b.preferred_language, country: b.country })

    const res = await sendTransactionalEmail({
      templateName: 'pre-checkout',
      recipientEmail: b.email,
      idempotencyKey: `review-request-${b.id}-${Date.now()}`,
      templateData: {
        guestName: b.name || '',
        reference: b.reference || '',
        checkIn: b.check_in,
        checkOut: b.check_out,
        reviewUrl: `https://casamipajobe.com/review/${token}`,
        locale,
        reviewOnly: true,
      },
    })
    if (!res.ok) throw new Error(res.reason ?? 'send_failed')

    await supabaseAdmin
      .from('bookings')
      .update({
        pre_checkout_email_sent_at: new Date().toISOString(),
        pre_checkout_email_status: 'sent',
        pre_checkout_email_message_id: res.messageId ?? null,
      })
      .eq('id', b.id)

    return { ok: true }
  })

export type PublicReview = {
  id: string
  rating: number
  title: string | null
  comment: string
  guestName: string
  country: string | null
  locale: string
  date: string // YYYY-MM
}

export const listPublishedReviews = createServerFn({ method: 'GET' }).handler(
  async (): Promise<PublicReview[]> => {
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { data, error } = await supabaseAdmin
      .from('reviews')
      .select(
        'id, rating, title, comment, guest_name, locale, created_at, booking:bookings(name, country, check_out)',
      )
      .eq('published', true)
      .order('created_at', { ascending: false })
      .limit(60)
    if (error) return []
    return (data ?? [])
      .filter((r: any) => (r.comment ?? '').trim().length > 0)
      .map((r: any) => {
        const raw = (r.guest_name || r.booking?.name || '').trim()
        const first = raw.split(/\s+/)[0] || 'Gast'
        const src = r.booking?.check_out || r.created_at
        return {
          id: r.id,
          rating: r.rating,
          title: r.title,
          comment: (r.comment ?? '').trim(),
          guestName: first,
          country: r.booking?.country ?? null,
          locale: r.locale || 'nl',
          date: String(src).slice(0, 7),
        }
      })
  },
)
